import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Formik, Form, Field } from 'formik'
import { useRouter } from 'next/router'
import styles from './styles.module.scss'
import Button from '../Button'
import CustomField from '../form/CustomField'
import IconListing from '/public/icons/icon-listing.svg'
import { useResponsive } from '../../../providers/ResponsiveContext'
import classNames from 'classnames'
import { useSearchContext } from '../../../providers/SearchContext'
import { withModifiers } from '../../helpers/withModifiers'
import isEmpty from 'lodash-es/isEmpty'
import IconHomeType from '/public/icons/icon-homeType.svg'
import IconPriceRange from '/public/icons/icon-price-range.svg'

// Import components directly to avoid issues with dynamic imports
import Dates from './components/Dates'
import Bedroom from './components/Bedrooms'
import Price from './components/Price'
import HomeType from './components/HomeType'
import Location from './components/Location'

const DateSearch = ({
  optional = false,
  custom = false,
  shadowRef,
  setIsActive,
  button,
  price,
  target = 'vacation-rentals',
  form,
  translations
}) => {
  const router = useRouter()
  const {
    search,
    setSearch,
    reset,
    initialValues,
    setSearchActive,
    setOffsetable
  } = useSearchContext()

  // State management - reduced number of state variables
  const [uiState, setUiState] = useState({
    prepend: translations?.listing?.[router.locale] || '',
    listing: search?.listing || '',
    priceActive: false,
    calendarActive: false,
    bedroomActive: false,
    listingActive: false,
    filtersActive: false,
    searchedUrl: 'vacation-rentals'
  })

  // Single reference for all active states to reduce re-renders
  const { prepend, listing } = uiState

  // Refs to prevent unnecessary re-renders
  const isFirstRender = useRef(true)
  const routerPathRef = useRef(router.asPath)
  const activeStateRef = useRef({})
  const fieldsCache = useRef({})

  const { isMobile, isTablet } = useResponsive()

  // Memoize form initial values
  const formInitialValues = useMemo(
    () => ({
      startDate: initialValues.startDate,
      endDate: initialValues.endDate,
      bedrooms: initialValues.bedrooms,
      listing: initialValues.listing,
      homeType: initialValues.homeType,
      amenities: initialValues.amenities,
      priceStart: initialValues.priceStart,
      priceEnd: initialValues.priceEnd,
      location: initialValues.location
    }),
    [] // Empty deps array - only compute once on initial render
  )

  // Handlers with useCallback to avoid recreating functions
  const updateUiState = useCallback((updates) => {
    setUiState((prev) => ({ ...prev, ...updates }))
  }, [])

  const handleVacationSearch = useCallback(() => {
    if (setSearchActive && target === 'vacation-rentals') {
      setSearchActive(true)
    }
    updateUiState({ searchedUrl: 'vacation-rentals' })
  }, [setSearchActive, target, updateUiState])

  const handleRealEstateSearch = useCallback(() => {
    updateUiState({ searchedUrl: 'real-estate' })
    if (setSearchActive && target !== 'vacation-rentals') {
      setSearchActive(true)
    }
  }, [setSearchActive, target, updateUiState])

  const handleSubmit = useCallback(
    (values, { setSubmitting }) => {
      setSubmitting(true)

      // Update the search state based on priceActive flag
      const newSearchValues = uiState.priceActive
        ? { ...initialValues, ...values }
        : {
            ...initialValues,
            ...values,
            startDate: values.startDate?.toISOString() || null,
            endDate: values.endDate?.toISOString() || null,
            listing: values.listing === '' ? null : values.listing,
            priceStart: null,
            priceEnd: null
          }

      // Determine route based on target and searchedUrl
      let reroute =
        uiState.searchedUrl !== 'vacation-rentals'
          ? '/real-estate/search'
          : target === 'vacation-rentals'
          ? `/${target}`
          : `/${target}/search`

      // Filter out null values for query params
      const filteredParams = Object.fromEntries(
        Object.entries(newSearchValues).filter(([key, value]) => {
          return value != null && !(key === 'sorting' && value === 'default')
        })
      )

      // Use router.push with shallow routing when appropriate
      router.push(
        {
          pathname: reroute,
          query: filteredParams,
          shallow: router.pathname === reroute
        },
        undefined,
        { shallow: router.pathname === reroute }
      )

      setSubmitting(false)
    },
    [uiState.priceActive, uiState.searchedUrl, initialValues, target, router]
  )

  // Update active state ref to avoid excessive re-renders
  useEffect(() => {
    activeStateRef.current = {
      calendarActive: uiState.calendarActive,
      bedroomActive: uiState.bedroomActive,
      listingActive: uiState.listingActive,
      priceActive: uiState.priceActive,
      filtersActive: uiState.filtersActive
    }

    // Notify parent of active state
    if (setIsActive) {
      const isAnyFieldActive = Object.values(activeStateRef.current).some(
        (value) => value
      )
      setIsActive(isAnyFieldActive)
    }
  }, [
    uiState.calendarActive,
    uiState.bedroomActive,
    uiState.listingActive,
    uiState.priceActive,
    uiState.filtersActive,
    setIsActive
  ])

  // Handle homepage reset
  useEffect(() => {
    const currentPath = router.asPath
    const prevPath = routerPathRef.current

    // Only run this if we're newly navigating to the homepage
    if (currentPath === '/' && prevPath !== '/' && currentPath !== prevPath) {
      setSearch(initialValues)
      setOffsetable(0)
    }

    // Update ref with latest path
    routerPathRef.current = currentPath
  }, [router.asPath, initialValues, setSearch, setOffsetable])

  // Handle locale changes and reset
  useEffect(() => {
    const newPrepend = translations?.listing?.[router.locale] || ''

    // Update prepend on reset or locale change if needed
    if (reset || (!listing && prepend !== newPrepend)) {
      updateUiState({
        listing: reset ? initialValues.listing : listing,
        prepend: newPrepend
      })
    }
  }, [
    router.locale,
    translations,
    reset,
    initialValues.listing,
    listing,
    prepend,
    updateUiState
  ])

  // Pre-render field components to avoid re-creation on each render
  const renderField = useCallback(
    (field, index, values, setFieldValue) => {
      // Use cached field if available to prevent re-renders
      const cacheKey = `${field}-${index}`

      // Only create new component if not cached or values changed
      if (
        !fieldsCache.current[cacheKey] ||
        values !== fieldsCache.current[`${cacheKey}-values`]
      ) {
        const commonProps = {
          key: `field-${index}`,
          values,
          shadowRef,
          translations
        }

        let component
        switch (field) {
          case 'homeType': {
            component = (
              <Field
                {...commonProps}
                component={HomeType}
                target={target}
                prependIcon={<IconHomeType width={26} height={24} />}
                width={isTablet ? 7.1 : 9}
                setFiltersActive={(active) =>
                  updateUiState({ filtersActive: active })
                }
              />
            )
            break
          }
          case 'priceRange': {
            if (price) {
              component = (
                <Field
                  {...commonProps}
                  component={Price}
                  price={price}
                  translation={translations?.priceRange?.[router.locale]}
                  prependIcon={<IconPriceRange width={26} height={24} />}
                  width={isTablet ? 8 : 11}
                  setPriceActive={(active) =>
                    updateUiState({ priceActive: active })
                  }
                />
              )
            }
            break
          }
          case 'dates': {
            component = (
              <Field
                {...commonProps}
                setFieldValue={setFieldValue}
                component={Dates}
                custom={custom}
                icon
                setCalendarActive={(active) =>
                  updateUiState({ calendarActive: active })
                }
              />
            )
            break
          }
          case 'location': {
            component = (
              <Field
                {...commonProps}
                setFieldValue={setFieldValue}
                component={Location}
                custom={custom}
                icon
                setCalendarActive={(active) =>
                  updateUiState({ calendarActive: active })
                }
              />
            )
            break
          }
          case 'bedrooms': {
            component = (
              <Field
                {...commonProps}
                setFieldValue={setFieldValue}
                component={Bedroom}
                custom={custom}
                icon
                width={isTablet ? 7.8 : 11}
                setBedroomActive={(active) =>
                  updateUiState({ bedroomActive: active })
                }
              />
            )
            break
          }
          case 'listings': {
            component = (
              <CustomField
                aria-label="Search listing by name"
                key={`field-${index}`}
                name="listing"
                prependIcon={<IconListing width={26} height={26} />}
                type="text"
                width={
                  listing !== ''
                    ? isTablet
                      ? 5
                      : 12.93
                    : isTablet
                    ? 5
                    : 10.876
                }
                variant={[isMobile && 'full-width', 'optional']}
                translations={translations}
                optional={optional}
                value={listing}
                onChange={(e) => {
                  setFieldValue('listing', e.target.value)
                  updateUiState({
                    listing: e.target.value,
                    listingActive: e.target.value !== ''
                  })
                }}
                isActive={listing !== ''}
                prepend={prepend}
                onFocus={() => {
                  updateUiState({ prepend: '' })
                }}
                onBlur={() => {
                  if (listing === '') {
                    updateUiState({
                      prepend: translations?.listing?.[router.locale] || ''
                    })
                  }
                }}
              />
            )
            break
          }
        }

        if (component) {
          fieldsCache.current[cacheKey] = component
          fieldsCache.current[`${cacheKey}-values`] = values
        }
      }

      return fieldsCache.current[cacheKey] || null
    },
    [
      shadowRef,
      target,
      translations,
      isTablet,
      price,
      router.locale,
      custom,
      isMobile,
      optional,
      listing,
      prepend,
      updateUiState
    ]
  )

  return (
    <div className={classNames(styles.search, custom ? styles.custom : '')}>
      <Formik
        initialValues={formInitialValues}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ values, setFieldValue }) => {
          // Calculate button active state once
          const buttonActive = useMemo(
            () =>
              values.bedrooms ||
              values.startDate ||
              values.endDate ||
              values.listing !== '' ||
              !isEmpty(values.homeType) ||
              !isEmpty(values.location) ||
              (values.priceStart && values.priceStart !== 0) ||
              (values.priceEnd && values.priceEnd !== price) ||
              !isEmpty(values.amenities),
            [values, price]
          )

          // Handle search context updates via effect
          useEffect(() => {
            if (search?.listing !== listing) {
              setFieldValue('listing', search.listing)
              updateUiState({
                listing: search.listing,
                prepend: search.listing
                  ? ''
                  : translations?.listing?.[router.locale] || ''
              })
            }
          }, [search.listing]) // Only depend on search.listing

          // Handle form mounting once
          useEffect(() => {
            if (isFirstRender.current) {
              isFirstRender.current = false
            }
          }, [])

          return (
            <Form className={styles.form}>
              <div className={styles.wrapper}>
                {form?.map((field, index) =>
                  renderField(field, index, values, setFieldValue)
                )}

                <Button
                  variant={[
                    'xs',
                    'secondary',
                    'gold',
                    '-offset-right',
                    isMobile && 'full-width'
                  ]}
                  onClick={handleVacationSearch}
                  className={withModifiers(
                    'submit',
                    [!buttonActive && 'disabled'],
                    styles
                  )}
                >
                  <span className={styles.submit__label}>
                    {translations?.search?.[router.locale]}
                  </span>
                </Button>
              </div>

              {button && (
                <Button
                  className={styles.button}
                  variant={['secondary']}
                  onClick={handleRealEstateSearch}
                >
                  {translations?.buyOrSell?.[router.locale]}
                </Button>
              )}
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default React.memo(DateSearch)
